import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TokenDelivery } from '../../types/tokenDelivery';
import { tokenDeliveryService } from '../../services/tokenDeliveryService';
import TokenDeliveryForm from './TokenDeliveryForm';

const TokenDeliveriesPage: React.FC = () => {
  const { t } = useTranslation();
  const [tokenDeliveries, setTokenDeliveries] = useState<TokenDelivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedTokenDelivery, setSelectedTokenDelivery] = useState<TokenDelivery | undefined>(undefined);

  // Function to fetch all token-delivery connections
  const fetchTokenDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tokenDeliveryService.getAllTokenDeliveries();
      setTokenDeliveries(data);
    } catch (error) {
      console.error('Error fetching token-delivery connections:', error);
      message.error(t('tokenDeliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load token-deliveries on component mount
  useEffect(() => {
    fetchTokenDeliveries();
  }, [fetchTokenDeliveries]);

  // Handle editing a token-delivery connection
  const handleEdit = (tokenDelivery: TokenDelivery) => {
    setSelectedTokenDelivery(tokenDelivery);
    setFormVisible(true);
  };

  // Handle creating a new token-delivery connection
  const handleCreate = () => {
    setSelectedTokenDelivery(undefined);
    setFormVisible(true);
  };

  // Handle deleting a token-delivery connection
  const handleDelete = async (id: number) => {
    try {
      await tokenDeliveryService.deleteTokenDelivery(id);
      message.success(t('tokenDeliveries.notifications.delete_success'));
      fetchTokenDeliveries();
    } catch (error) {
      console.error('Error deleting token-delivery connection:', error);
      message.error(t('tokenDeliveries.notifications.error', { message: (error as Error).message }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchTokenDeliveries();
  };

  // Helper function to truncate token for display
  const truncateToken = (token: string) => {
    if (token.length > 20) {
      return token.substring(0, 20) + '...';
    }
    return token;
  };

  // Get existing token IDs (for filtering in form)
  const existingTokenIds = tokenDeliveries.map(td => td.token_id);

  // Table columns
  const columns = [
    {
      title: t('tokenDeliveries.list.token'),
      dataIndex: 'token_value',
      key: 'token_value',
      render: (text: string) => truncateToken(text),
    },
    {
      title: t('tokenDeliveries.list.comment'),
      dataIndex: 'token_comment',
      key: 'token_comment',
      render: (text: string | null) => text || '-',
    },
    {
      title: t('tokenDeliveries.list.delivery'),
      dataIndex: 'delivery_name',
      key: 'delivery_name',
      render: (text: string | null) => text || '-',
    },
    {
      title: t('tokenDeliveries.list.domain'),
      dataIndex: 'delivery_domain',
      key: 'delivery_domain',
    },
    {
      title: t('tokenDeliveries.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: t('tokenDeliveries.list.actions'),
      key: 'actions',
      render: (_: unknown, record: TokenDelivery) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('tokenDeliveries.actions.edit')}
          </Button>
          <Popconfirm
            title={t('tokenDeliveries.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('tokenDeliveries.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('tokenDeliveries.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('tokenDeliveries.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tokenDeliveries}
        rowKey="id"
        loading={loading}
      />

      <TokenDeliveryForm
        tokenDelivery={selectedTokenDelivery}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
        existingTokenIds={existingTokenIds}
      />
    </Card>
  );
};

export default TokenDeliveriesPage;
